const axios = require('axios');
const CheckoutStage = require('@/lib/Checkout/CheckoutStage');
const EInvoicingFieldRefEnum = require('@/lib/Invoicing/EInvoicingFieldRefEnum');
const EInvoicingInvoiceStatusEnum = require('@/lib/Invoicing/EInvoicingInvoiceStatusEnum');
const logFactory = require('@/libraries/DigiTickets/Logging/logFactory');
const { clearLoadingMessage, setLoadingMessage } = require('@/functions/ui');

/**
 * @typedef {object} EInvoicingSignPaymentResult
 * @property {string} uid - UID (article 5 Α11389/2020).
 * @property {string} pid - The unique Payment ID provided by Entersoft.
 * @property {string} sig - Signature for the payment.
 * @property {string} tidnsp - NSP's unique identity. (Payment terminal TID)
 * @property {string} sigd - Date of the signature (e.g. 2025-04-08T17:03:13.6209288 in Greece local time)
 * @property {string} extid - The externalID we sent to Entersoft with the sign request.
 */

/**
 * This class handles communication with the "eInvoicing Service".
 * That is currently only used for producing invoices to send to an API from https://www.entersoft.eu/
 * for Greek tax purposes.
 * See https://bitbucket.org/digitickets/einvoicing-service for some more info.
 */
class EInvoicingService {
    /**
     * @param {AppConfig} appConfig
     * @param {FieldDataService} fieldDataService
     * @param {ToastFactory} toastFactory
     * @param {UserService} userService
     */
    constructor(
        appConfig,
        fieldDataService,
        toastFactory,
        userService
    ) {
        this.appConfig = appConfig;
        this.fieldDataService = fieldDataService;
        this.toastFactory = toastFactory;
        this.userService = userService;

        this.axios = axios.create({
            baseURL: this.appConfig.eInvoicingUrl,
        });

        // Add request interceptor to add the Authorization header at the point a request is made.
        // If we add it in the axios.create() call, we read the value of getApiKey() too soon, potentially
        // before a user has logged in.
        this.axios.interceptors.request.use(
            (config) => {
                // Add the Authorization header if we have an API key.
                const apiKey = this.userService.getApiKey();
                if (apiKey) {
                    config.headers.Authorization = `Bearer ${apiKey}`;
                }
                return config;
            },
            (error) => Promise.reject(error)
        );

        // We store some data about invoicing in specific custom field that should exist for the company.
        // The eInvoicing service has a route to get all the relevant custom fields for the company,
        // and it also validates all the necessary fields exist and are set up correctly.
        this.customFieldIDs = {};
        this.customFieldOptionIDs = {};

        this.log = logFactory('EInvoicingService');
    }

    /**
     * @return {boolean}
     */
    inUseForCompany() {
        let entersoftConfig = this.userService.getCompany().settings.entersoftConfig;
        if (!entersoftConfig) {
            return false;
        }

        try {
            entersoftConfig = JSON.parse(entersoftConfig);
        } catch (e) {
            this.log.error(
                'Invalid JSON in Entersoft config.',
                e.message,
                this.userService.getCompany().settings.entersoftConfig
            );
            return false;
        }

        if (entersoftConfig.disabled) {
            this.log.warn('Entersoft is disabled by the configuration.');
            return false;
        }

        // They have a valid config, and the config is not disabling it, so we're on!
        this.log.debug('Entersoft is enabled.');
        return true;
    }

    async initialize() {
        const spinner = this.toastFactory.spinner('EPOS_INDEX.EINVOICING_INIT');

        try {
            // Check unconfirmed order flow is enabled.
            if (!this.userService.getCompany().settings.enablePropointUnconfirmedOrderFlow) {
                throw new Error(
                    'The unconfirmed order flow must be enabled to use eInvoicing.'
                );
            }

            // Orders have some custom fields that are used to store information about invoices and credit notes.
            // We need to know the field IDs of these fields to write to them in some cases such as communication
            // errors with the eInvoicing service.
            // Asking the eInvoicing service for the fieldIDs will cause it to report any errors with the field
            // configuration too to make it easy to see if anything is wrong.
            await this.loadCustomFieldIDs();

            this.toastFactory.success('EPOS_INDEX.EINVOICING_INIT_SUCCESS');
        } catch (e) {
            this.log.error(e);
            this.toastFactory.errorTop(
                'EPOS_INDEX.EINVOICING_INIT_ERROR',
                e.message
            );
        } finally {
            this.toastFactory.clear(spinner);
        }
    }

    /**
     * @private
     *
     * @returns {Promise}
     */
    async loadCustomFieldIDs() {
        return this.axios
            .get('/fields')
            .then((response) => {
                /**
                 * @type {GetCustomFieldsResponse}
                 */
                const data = response.data;

                // Store the field IDs for later use.
                data.fields?.forEach((field) => {
                    this.customFieldIDs[field.ref] = field.fieldID;

                    // If the field is a select field, store the option IDs too.
                    if (field.options?.length > 0) {
                        this.customFieldOptionIDs[field.ref] = {};
                        field.options.forEach((option) => {
                            this.customFieldOptionIDs[field.ref][option.name] = option.fieldOptionID;
                        });
                    }
                });
                this.log.debug('Loaded custom fields from eInvoicing service.', this.customFieldIDs);

                // Display any errors reported about custom fields.
                if (data.errors?.length) {
                    this.log.error('The eInvoicing service reported some custom field errors.', data.errors);

                    // Show the errors to the user.
                    const errorMessage = data.errors.join(' ');
                    throw new Error(errorMessage);
                }

                return this.customFieldIDs;
            });
    }

    /**
     * @param {Order} order
     *
     * @param {object} params
     *
     * @returns {Promise<SubmitInvoiceResponse>}
     */
    async submitInvoice(order, params) {
        const bookingRef = order.getOriginalRef();

        let url = `bookings/${bookingRef}/invoice/submit`;
        if (params) {
            url += '?' + new URLSearchParams(params);
        }

        return this.axios
            .post(url)
            .then(async (response) => {
                /**
                 * @type {SubmitInvoiceResponse}
                 */
                const data = response.data;
                this.log.debug('Submit invoice response', data);

                if (!data.success && !data.offlineInvoiceUrl) {
                    throw new Error('Bad response from eInvoicing service');
                }

                return data;
            })
            .catch(async (err) => {
                this.log.error('Error submitting invoice', err, err.response?.data);
                if (err.response?.data?.message) {
                    err.message = err.message + ' ' + err.response.data.message;
                }

                // The service tells us if it updated the order's custom fields itself.
                // In normal operation, the service will store the results of the invoice submission in custom fields,
                // including if there was any error returned by Entersoft. However, in the event there is a problem
                // with communication between ProPoint and the eInvoicing service, or there is an error in the
                // eInvoicing service itself, we also want to record this against the order. So we'll do that here
                // if we haven't been told by the service that it's been done.
                const orderFieldsUpdated = !!err?.response?.data?.orderFieldsUpdated;
                if (!orderFieldsUpdated) {
                    await this.setInvoiceFieldsFromError(err, order);
                }

                throw err;
            });
    }

    /**
     * @param {Order} order
     *
     * @param {object} params
     *
     * Note the return value is a SubmitInvoiceResponse because credit notes are also invoices.
     * @returns {Promise<SubmitInvoiceResponse>}
     */
    async submitCreditNote(order, params = {}) {
        const bookingRef = order.getOriginalRef();

        let url = `bookings/${bookingRef}/credit-note/submit`;
        if (params) {
            url += '?' + new URLSearchParams(params);
        }

        return this.axios
            .post(url)
            .then(async (response) => {
                /**
                 * @type {SubmitInvoiceResponse}
                 */
                const data = response.data;
                this.log.debug('Submit credit note response', data);

                if (!data.success && !data.offlineInvoiceUrl) {
                    throw new Error('Bad response from eInvoicing service');
                }

                return data;
            })
            .catch(async (err) => {
                this.log.error('Error submitting credit note', err, err.response?.data);
                if (err.response?.data?.message) {
                    err.message = err.message + ' ' + err.response.data.message;
                }

                // The service tells us if it updated the order's custom fields itself.
                // In normal operation, the service will store the results of the invoice submission in custom fields,
                // including if there was any error returned by Entersoft. However, in the event there is a problem
                // with communication between ProPoint and the eInvoicing service, or there is an error in the
                // eInvoicing service itself, we also want to record this against the order. So we'll do that here
                // if we haven't been told by the service that it's been done.
                const orderFieldsUpdated = !!err?.response?.data?.orderFieldsUpdated;
                if (!orderFieldsUpdated) {
                    await this.setCreditNoteFieldsFromError(err, order);
                }

                throw err;
            });
    }

    /**
     * Create a (Greek Tax) invoice for the order if appropriate.
     * Errors are suppressed (with a toast) so a failure does not fail the whole operation.
     *
     * @param {Order} order
     *
     * @returns {Promise}
     */
    async createInvoiceIfNeeded(order) {
        if (!this.inUseForCompany()) {
            return;
        }

        if (this.getInvoiceUrlFromOrder(order)) {
            // An invoice was already created for this order.
            return;
        }

        try {
            this.log.debug('Creating invoice.');

            const invoiceResponse = await this.submitInvoiceOrCreditNote(order);

            this.addInvoiceToReceipt(order, invoiceResponse);
        } catch (error) {
            this.log.error('Error creating invoice.', error);

            // We'll suppress errors creating the invoice.
            // Show a warning toast but don't fail the wider operation.
            this.toastFactory.errorTop(
                'CHECKOUT_ERROR_TITLES.CREATE_INVOICE',
                error.message
            );
        }
    }

    /**
     * Create a credit note for a refund that was processed as an order adjustment, if appropriate.
     * Errors are suppressed in the same way as createInvoiceIfNeeded.
     *
     * @param {OrderAdjustmentResponse} orderAdjustmentResponse
     * @param {Order} receiptOrder The order whose receipt the credit note URL should be added to.
     *
     * @returns {Promise}
     */
    async createAdjustmentCreditNoteIfNeeded(orderAdjustmentResponse, receiptOrder) {
        if (!this.inUseForCompany()) {
            return;
        }

        try {
            this.log.debug('Creating invoice.');
            setLoadingMessage('CHECKOUT_PROCESSING_MESSAGES.' + CheckoutStage.CREATE_INVOICE);

            if (orderAdjustmentResponse.adjustmentOrder.total === 0) {
                // We don't issue credit notes for zero-value adjustments.
                return;
            }

            let invoiceResponse;

            if (orderAdjustmentResponse.adjustmentOrder.total < 0) {
                // A refund was processed via an order edit - the expected way for refunds to happen.
                // Issue a credit note for the refund.
                // Note that there is code in place in OrderValidator to prevent more than one refund adjustment
                // being created per booking.
                invoiceResponse = await this.submitCreditNote(
                    orderAdjustmentResponse.originalOrder
                );
            }

            if (!invoiceResponse) {
                // Nothing was issued (a positive adjustment), so there is nothing to add to the receipt.
                return;
            }

            this.addInvoiceToReceipt(receiptOrder, invoiceResponse);
        } catch (error) {
            this.log.error('Error creating invoice.', error);

            // We'll suppress errors creating the invoice.
            // Show a warning toast but don't block the order flow.
            this.toastFactory.errorTop(
                'CHECKOUT_ERROR_TITLES.CREATE_INVOICE',
                error.message
            );
        } finally {
            clearLoadingMessage();
        }
    }

    /**
     * Submit an invoice (or a credit note for a negative order total) for a newly confirmed order.
     *
     * @private
     *
     * @param {Order} order
     *
     * @returns {Promise<SubmitInvoiceResponse>}
     */
    async submitInvoiceOrCreditNote(order) {
        setLoadingMessage('CHECKOUT_PROCESSING_MESSAGES.' + CheckoutStage.CREATE_INVOICE);

        try {
            if (order.getTotal() < 0) {
                // Refunds processed by selling negative quantities in a new order get a credit note
                // without an original invoice. Entersoft supports this kind of credit note without an
                // original invoice in retail environments.
                return await this.submitCreditNote(
                    order,
                    {
                        processInvoiceAsCreditNote: 1,
                    }
                );
            }

            return await this.submitInvoice(order);
        } finally {
            clearLoadingMessage();
        }
    }

    /**
     * Put the invoice (or credit note) URL on the order's receipt as a QR code, along with any
     * additional text the invoicing provider requires to be printed.
     *
     * @private
     *
     * @param {Order} order
     * @param {SubmitInvoiceResponse} invoiceResponse
     */
    addInvoiceToReceipt(order, invoiceResponse) {
        if (invoiceResponse.invoiceUrl) {
            order.addReceiptQRCodeUrl(invoiceResponse.invoiceUrl);
        } else if (invoiceResponse.offlineInvoiceUrl) {
            // If there is an offline QR code add that to the receipt.
            order.addReceiptQRCodeUrl(invoiceResponse.offlineInvoiceUrl);
        } else {
            throw new Error('No invoice URL was returned from the eInvoicing service.');
        }

        // If Entersoft sends this key, we have to print it as-is on the receipt.
        if (invoiceResponse.customerFacingText) {
            order.additionalReceiptText.push(invoiceResponse.customerFacingText);
        }
    }

    /**
     * @param {Order} order
     *
     * @returns {Promise<string|null>}
     */
    getInvoiceUrlFromOrder(order) {
        return this.getFieldValueFromOrder(EInvoicingFieldRefEnum.ENTERSOFT_INVOICE_URL, order);
    }

    /**
     * @param {Order} order
     *
     * @returns {Promise<string|null>}
     */
    getCreditNoteUrlFromOrder(order) {
        return this.getFieldValueFromOrder(EInvoicingFieldRefEnum.ENTERSOFT_CREDIT_NOTE_URL, order);
    }

    /**
     * @private
     *
     * @param {string} fieldRef
     * @param {Order} order
     *
     * @returns {Promise<string|null>}
     */
    getFieldValueFromOrder(fieldRef, order) {
        /**
         * @type {FieldInstance}
         */
        const fieldInstance = order.orderLevelFieldGroup?.instances.find(
            (fi) => fi.field.ref === fieldRef
        );

        if (fieldInstance && fieldInstance.value) {
            return fieldInstance.value;
        }

        return null;
    }

    async setInvoiceFieldsFromError(error, order) {
        const statusFieldRef = EInvoicingFieldRefEnum.ENTERSOFT_INVOICE_STATUS;
        if (!this.customFieldOptionIDs[statusFieldRef]) {
            throw new Error(`Field options not found for ref ${statusFieldRef}`);
        }
        const statusRef = EInvoicingInvoiceStatusEnum.FAILED;
        const fieldOptionID = this.customFieldOptionIDs[statusFieldRef][statusRef];
        if (!fieldOptionID) {
            throw new Error(`Field option ID not found for status ${statusRef}`);
        }

        await this.setFieldDataForOrder(statusFieldRef, fieldOptionID, order);

        const additionalMetadata = {
            invoices: {
                // We only support 1 invoice per order currently, so we're using ID 1 as we don't have a real ID.
                1: [
                    {
                        date: new Date().toISOString(),
                        status: EInvoicingInvoiceStatusEnum.FAILED,
                        source: 'ProPoint',
                        message: error.message,
                    },
                ],
            },
        };

        const metadataFieldRef = EInvoicingFieldRefEnum.ENTERSOFT_METADATA;
        const metadataFieldID = this.customFieldIDs[metadataFieldRef];
        await this.fieldDataService.appendJsonFieldDataForOrderField(
            order.ID,
            metadataFieldID,
            additionalMetadata
        );
    }

    async setCreditNoteFieldsFromError(error, order) {
        const statusFieldRef = EInvoicingFieldRefEnum.ENTERSOFT_CREDIT_NOTE_STATUS;
        if (!this.customFieldOptionIDs[statusFieldRef]) {
            throw new Error(`Field options not found for ref ${statusFieldRef}`);
        }
        const statusRef = EInvoicingInvoiceStatusEnum.FAILED;
        const fieldOptionID = this.customFieldOptionIDs[statusFieldRef][statusRef];
        if (!fieldOptionID) {
            throw new Error(`Field option ID not found for status ${statusRef}`);
        }

        await this.setFieldDataForOrder(statusFieldRef, fieldOptionID, order);

        const additionalMetadata = {
            creditNotes: {
                // We only support 1 credit note per order currently, so we're using ID 1 as we don't have a real ID.
                1: [
                    {
                        date: new Date().toISOString(),
                        status: EInvoicingInvoiceStatusEnum.FAILED,
                        source: 'ProPoint',
                        message: error.message,
                    },
                ],
            },
        };

        const metadataFieldRef = EInvoicingFieldRefEnum.ENTERSOFT_METADATA;
        const metadataFieldID = this.customFieldIDs[metadataFieldRef];
        await this.fieldDataService.appendJsonFieldDataForOrderField(
            order.ID,
            metadataFieldID,
            additionalMetadata
        );
    }

    /**
     * For the most part, the eInvoicing service will handle storing the results of the invoice submission
     * in custom fields on the order.
     * However, if we have an error communicating with the eInvoicing service, we may want to store that too.
     * So in some cases ProPoint will be the one to set the custom field data.
     *
     * @param {string} fieldRef
     * @param {string} value
     * @param {Order} order
     */
    async setFieldDataForOrder(fieldRef, value, order) {
        const fieldID = this.customFieldIDs[fieldRef];
        if (!fieldID) {
            throw new Error(`Field ID not found for ref ${fieldRef}`);
        }

        const data = {
            order: {
                [fieldID]: value,
            },
        };

        this.log.log('Updating order field data', data);

        return this.fieldDataService.updateFieldDataForOrder(order.ID, data);
    }

    async appendFieldDataForOrder(fieldRef, value, order) {
        const fieldID = this.customFieldIDs[fieldRef];
        if (!fieldID) {
            throw new Error(`Field ID not found for ref ${fieldRef}`);
        }

        const data = {
            order: {
                [fieldID]: value,
            },
        };

        this.log.log('Updating order field data', data);

        return this.fieldDataService.appendFieldDataForOrder(order.ID, data);
    }

    /**
     * @param {EInvoicingSignPaymentRequest} requestData
     *
     * @returns {Promise<SignPaymentResponse>}
     */
    async signPayment(requestData) {
        return this.axios
            .post(
                'payments/sign',
                requestData
            )
            .then((response) => {
                /**
                 * @type {SignPaymentResponse}
                 */
                const data = response.data;
                this.log.debug('Sign payment response', data);

                if (!data.success) {
                    console.error(data);
                    throw new Error('Bad response from eInvoicing service');
                }

                if (!data.entersoftResponse.pnf.dsig.sv) {
                    throw new Error('No signature returned from eInvoicing service');
                }

                return data;
            })
            .catch((err) => {
                console.error(err);
                console.log(err.response);

                let errorMessage = 'Error signing payment.';
                if (err.response?.data?.message) {
                    errorMessage += ' ' + err.response.data.message;
                }
                throw new Error(errorMessage);
            });
    }
}

module.exports = EInvoicingService;
